"use client";

import React from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const quickFacts = ["PDA is a neuro-biological profile within the autism spectrum.", "Driven by an intense, anxiety-based need for autonomy and self-governance.", "Traditional 'consequence-based' discipline typically escalates the threat response.", "PDAers often 'mask' their struggles in public, leading to exhaustion at home.", "Collaborative, flexible, and trust-based approaches are the most effective."];
export default function UnderstandingPda() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="160704" id="pathways-to-understanding" className="py-24 bg-background overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="flex flex-col justify-center h-full">
            <h2 className="text-4xl md:text-5xl font-serif text-primary mb-8 leading-tight">
              Pathways to Understanding PDA
            </h2>
            
            <div className="space-y-6 text-lg text-foreground/90 font-sans leading-relaxed mb-10">
              <p>
                Pathological Demand Avoidance (PDA) is a specific profile of autism that requires a fundamental shift in how we approach support and education. At PDA Australia, we provide the pathways to bridge the gap between traditional understanding and the lived reality of PDA individuals.
              </p>
              <p>
                For a PDAer, everyday requests are perceived as threats to their autonomy, triggering a fight-flight-freeze response in the nervous system. This isn't a choice or a behavioral issue; it is a physiological reaction to perceived loss of control triggered by a demand.
              </p>
              <p>
                Our mission is to equip Australian parents, teachers, and professionals with the tools to look beyond the behavior. By adopting a "low-demand" lifestyle and prioritizing trust and collaboration, we can create environments where PDA individuals truly flourish.
              </p>
            </div>

            <div>
              <Button asChild size="lg" className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 px-8 shadow-md">
                <Link to="/ArticlesPage">
                  Explore Our Resources
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:pt-4">
            <Card className="border-2 border-secondary bg-card shadow-xl shadow-shadow-color/5 rounded-2xl overflow-hidden h-full flex flex-col">
              <CardHeader className="bg-secondary/20 pb-6 border-b border-secondary/30 px-8 pt-8">
                <CardTitle className="text-2xl md:text-3xl font-serif text-primary flex items-center gap-3">
                  Deep Dive: Quick Facts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 flex-grow">
                <ul className="space-y-6">
                  {quickFacts.map((fact, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                      <div className="mt-1 bg-background rounded-full p-0.5 shrink-0 shadow-sm">
                        <CircleCheck className="w-6 h-6 text-primary" />
                      </div>
                      <span className="text-foreground/90 font-sans text-lg leading-snug">
                        {fact}
                      </span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}